import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { partnerInventoryEventsQuery } from '../../lib/rescueQueries';
import type { partnerInventoryEvents, partnerInventoryEventsVariables } from '@/types/gql-op-types';
import styles from './InventoryHistoryModal.module.scss';

const PAGE_SIZE = 50;

const LIFECYCLE_BY: Record<string, string> = {
  PARTNER: 'by your rescue',
  ADMIN: 'by Fi',
  RMA_DISCARD: 'by a Fi return',
  RMA_RECEIVED: 'by a Fi return',
};

const LEDGER_DETAIL: Record<string, string> = {
  SHIPPED: 'Shipped by Fi',
  DELIVERED: 'Delivered',
  BACKFILL: 'Recorded when tracking began',
  ADOPTION_ACCEPTED: 'The adopter accepted the transfer',
  ADOPTION_RESOLVED: 'Adoption closed by your rescue',
  ADOPTION_RECOVERED: 'Recovered from the adopter',
  COLLAR_RETURNED: 'Returned from the adopter',
  RELEASED_OUTSIDE_PARTNER: 'Moved to a pet outside your rescue',
  RMA_RECEIVED: 'Received by Fi in a return',
  RECONCILED: 'Corrected by Fi',
  ORDER_REASSIGNED: 'Moved to your rescue by Fi',
  ADDED_BY_PARTNER: 'by your rescue',
};

/** The API stores reasons as free text, so a reason added later still reads as words until it's mapped. */
function humanize(code: string): string {
  const words = code.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeInventoryEvent(event: {
  kind: string;
  fromStatus?: string | null;
  toStatus: string;
  reason: string;
}): { title: string; detail: string } {
  if (event.kind === 'LIFECYCLE') {
    const detail = LIFECYCLE_BY[event.reason] ?? humanize(event.reason);
    switch (event.toStatus) {
      case 'LOST':
        return { title: 'Marked lost', detail };
      case 'INOPERABLE':
        return { title: 'Marked inoperable', detail };
      default:
        return { title: event.fromStatus === 'LOST' ? 'Marked found' : 'Restored to service', detail };
    }
  }
  const detail = LEDGER_DETAIL[event.reason] ?? humanize(event.reason);
  switch (event.toStatus) {
    case 'ADOPTED':
      return { title: 'Went home with an adopter', detail };
    case 'RETURNED_TO_FI':
      return { title: 'Returned to Fi', detail };
    default:
      return { title: event.fromStatus ? 'Back with your rescue' : 'Added to your inventory', detail };
  }
}

function formatOccurredAt(iso: string): string {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : iso;
}

interface InventoryHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

export default function InventoryHistoryModal({ open, onClose }: InventoryHistoryModalProps) {
  const [loadingMore, setLoadingMore] = useState(false);
  const { data, loading, error, fetchMore } = useQuery<partnerInventoryEvents, partnerInventoryEventsVariables>(
    partnerInventoryEventsQuery,
    { variables: { input: { first: PAGE_SIZE } }, skip: !open, fetchPolicy: 'cache-and-network' },
  );
  const connection = data?.partnerInventoryEvents;
  const events = connection?.events ?? [];

  async function handleLoadMore() {
    if (!connection?.endCursor || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: { input: { first: PAGE_SIZE, after: connection.endCursor } },
        updateQuery: (prev, { fetchMoreResult }) => ({
          partnerInventoryEvents: {
            ...fetchMoreResult.partnerInventoryEvents,
            events: [...prev.partnerInventoryEvents.events, ...fetchMoreResult.partnerInventoryEvents.events],
          },
        }),
      });
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="wide">
      <ModalTitle>Device history</ModalTitle>
      <ModalSubtitle>Every change to your rescue&rsquo;s devices, newest first.</ModalSubtitle>

      {loading && events.length === 0 && <div className={styles.empty}>Loading…</div>}
      {error && events.length === 0 && (
        <div className={styles.empty} role="alert">
          Could not load history.
        </div>
      )}
      {!loading && !error && events.length === 0 && <div className={styles.empty}>No history yet.</div>}

      {events.length > 0 && (
        <ul className={styles.list}>
          {events.map((event) => {
            const { title, detail } = describeInventoryEvent(event);
            return (
              <li key={event.id} className={styles.row}>
                <div className={styles.rowTitle}>
                  <span>
                    <strong>{event.moduleId}</strong> &middot; {title}
                  </span>
                  <span className={styles.rowMeta}>{formatOccurredAt(event.occurredAt)}</span>
                </div>
                <div className={styles.rowMeta}>{detail}</div>
                {event.note && <div className={styles.note}>{event.note}</div>}
              </li>
            );
          })}
        </ul>
      )}

      {connection?.hasMore && (
        <div className={styles.loadMore}>
          <Button variant="ghost" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      </ModalActions>
    </Modal>
  );
}
